import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CampaignSegment } from '../model/campaign-segment.model';
import { Category } from '../model/category.model';
import { CategoryService } from './category.service';
import 'anychart';
import { id } from '@swimlane/ngx-charts';

interface Segment {
  categoryIds: Category['id'][];
  metaCategoryIds: Category['meta_category'][];
  numb_mail: number;
  pourcentBase: number;
  pourcentVente: number;
  proba: number;
  list_id?: number[];
  optimal?: number;
}

export interface PostCampagneResponse {
  segments: Segment[];
  numb_all_mail: number;
  global_curve?: { x: number, y: number }[];
  infos_crm?: any;
  venn: {
    x: string[];
    value: number;
    meta_category: string[];
  }[];
}

export interface Campagne {
  segments: Segment[];
  numb_all_mail: number;
  venn: {
    id: string[];
    meta_category: string[];
    x: string[];
    value: number;
    label: {
      fontColor: string;
    };
  }[];
}

export interface SegmentMetadataItem {
  name: string;
  description: string;
  criteria: string;
  count: number;
  pct: number;
  avg_score: number;
  color: string;
  icon: string;
  // Champs optionnels selon le type de segment
  avg_reactivity?: number | null;
  avg_days_since_last_open?: number | null;
  avg_sent_count?: number | null;
  avg_nb_commandes?: number | null;
  avg_panier_moyen?: number | null;
  avg_montant_total?: number | null;
  avg_recence_jours?: number | null;
  avg_clv?: number | null;
}

export type SegmentMetadata = Record<string, SegmentMetadataItem[]>;

export interface SegmentMatrixCell {
  axis_type: string;
  segment_email: string;
  segment_y: string;
  count: number;
  count_j30: number;
  delta_pct: number | null;
  ca_30j?: number;
  segment_email_values?: string[];
  segment_y_values?: string[];
}

export interface SegmentMatrixResponse {
  snapshot_date: string | null;
  total_clients: number;
  total_ca_30j?: number;
  axis_types: string[];
  email_labels: string[];
  y_labels_by_axis: Record<string, string[]>;
  cells: SegmentMatrixCell[];
  email_label_mapping?: Record<string, string[]>; // grouped email label → original labels
  y_label_mapping?: Record<string, Record<string, string[]>>; // axis_type → grouped y label → original labels
}

interface SegmentAchats {
  segment_achats: string;
  count_size: number;
}

interface SegmentClients {
  segment_email: string;
  count_size: number;
}

interface AgePyramid {
  age_group: string;
  MME: number;
  MR: number;
}

interface GenreData {
  civilite: string;
  count: number;
  percentage: number;
}

// Now update the LiftCurveResponse interface
interface LiftCurveResponse {
  [key: string]: {
    ideal_pourcent_base: number | null;
    curve: number[] | null;
    error: string | null;
    infos_crm?: {
      segment_achats: SegmentAchats[];
      segment_clients: SegmentClients[];
      age_pyramid: AgePyramid[];
      realite_genre: GenreData[];
      segment_genre: GenreData[];
    };
  };
}


export interface ScheduledCampaign {
  id?: number;
  date: string; // YYYY-MM-DD
  name: string;
  segments: any[];
  status: 'DRAFT' | 'VALIDATED' | 'EXTRACTED';
  created_at?: string;
  updated_at?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CampaignService {
  constructor(private http: HttpClient, private categoryService: CategoryService) { }

  getLiftCurve(category: Category): Observable<{
    optimal: number | null;
    curve: { x: number; y: number }[] | null;
    error?: string;

  } | undefined> {
    return this.http
      .get<LiftCurveResponse>(
        `${environment.backendUrl}/api/campagnecurve/`,
        {
          params: {
            category: category.id?.toString() ?? '0',
            meta_category: category.meta_category?.toString() ?? '0',
          },
        },
      )
      .pipe(
        map((response) => {
          const categoryKey = `${category.id}`;
          const values = response[categoryKey];

          if (values) {
            if (values.error) {
              // Return the error to the component
              return {
                optimal: null,
                curve: null,
                error: values.error,
                infos_crm: undefined,
              };
            } else {
              return {
                optimal: values.ideal_pourcent_base,
                curve: values.curve
                  ? values.curve.map((value, index) => ({ x: index + 1, y: value }))
                  : null,
                error: undefined,
                infos_crm: values.infos_crm,
              };
            }
          } else {
            // Handle the case where the category is not found in the response
            return {
              optimal: null,
              curve: null,
              error: 'Category not found in response',
              infos_crm: undefined,
            };
          }
        }),
      );
  }

  getInfosCRM(category: Category, pct_base: number): Observable<{
    infos_crm?: {
      segment_achats: SegmentAchats[];
      segment_clients: SegmentClients[];
      age_pyramid: AgePyramid[];
      realite_genre: GenreData[];
      segment_genre: GenreData[];
    };
  } | undefined> {
    return this.http
      .get<LiftCurveResponse>(
        `${environment.backendUrl}/api/campagnecurve/`,
        {
          params: {
            category: category.id?.toString() ?? '0',
            meta_category: category.meta_category?.toString() ?? '0',
            infos_crm: true,
            pct_base: pct_base,
          },
        },
      )
      .pipe(
        map((response) => {
          const categoryKey = `${category.id}`;
          const values = response[categoryKey];

          if (values) {
            if (values.error) {
              // Return the error to the component
              return {
                optimal: null,
                curve: null,
                error: values.error,
                infos_crm: undefined,
              };
            } else {
              return {
                optimal: values.ideal_pourcent_base,
                curve: values.curve
                  ? values.curve.map((value, index) => ({ x: index + 1, y: value }))
                  : null,
                error: undefined,
                infos_crm: values.infos_crm,
              };
            }
          } else {
            // Handle the case where the category is not found in the response
            return {
              optimal: null,
              curve: null,
              error: 'Category not found in response',
              infos_crm: undefined,
            };
          }
        }),
      );
  }

  mergeCampaigns(capping: number, segments: CampaignSegment[]): Observable<Campagne> {
    return this.http
      .post<PostCampagneResponse>(`${environment.backendUrl}/api/campagne/`, {
        send: false,
        capping,
        segments: Object.values(
          segments.reduce(
            (
              res: {
                lists: { [key: number]: any[] };
                indexOfMerge: number | null;
              },
              segment: CampaignSegment,
              index: number,
            ) => {
              // Build an array of category objects for each segment
              const categories = segment.categories.map((category) => ({
                meta_category: category.category.meta_category, // Use meta_category from the category
                category: category.category.id,
                pctBase: category.percentage,
              }));

              if (segment.checked) {
                // Merge with existing segment if 'checked' is true
                const pos = res.indexOfMerge ?? index;
                res.lists[pos] = [...(res.lists[pos] || []), ...categories];
                res.indexOfMerge = pos;
              } else {
                // Create a new segment
                const pos = index;
                res.lists[pos] = categories;
              }

              return res;
            },
            {
              lists: {},
              indexOfMerge: null,
            },
          ).lists,
        ),
      })
      .pipe(
        map((res: PostCampagneResponse) => ({
          ...res,
          venn: res.venn.map((item: { x: string[]; meta_category: string[]; value: number }) => {
            // Prepare to map categories with their corresponding meta categories
            const xLabels: string[] = [];

            item.x.forEach((xStr, index) => {
              // Split xStr into ids
              const ids = xStr.split(',').map((id) => id.trim());
              // Get the corresponding meta_category string and split it
              const metaCatStr = item.meta_category[index];
              const metaCats = metaCatStr.split(',').map((mc) => mc.trim());

              // Check if ids and metaCats have the same length
              if (ids.length !== metaCats.length) {
                console.error('Mismatch between ids and meta categories:', ids, metaCats);
              }

              // Map ids and metaCats to labels
              const labels = ids.map((id, idx) => {
                const metaCat = parseInt(metaCats[idx]);
                const categoryLabel = this.categoryService.getCategoryById(parseInt(id), metaCat)?.label || '';
                return categoryLabel;
              });

              // Join the labels and add to xLabels array
              xLabels.push(labels.join(', '));
            });

            return {
              id: item.x,
              x: xLabels,
              value: item.value,
              meta_category: item.meta_category,
              label: {
                fontColor: '#212529',
                fontWeight: 'normal',
                fontFamily: 'Open Sans, sans-serif',
                fontSize: '15px'
              },
            };
          }),
        })),
      );
  }

  generateSegmentsText(capping: number, segments: CampaignSegment[]): Observable<string> {
    return this.http.post(
      `${environment.backendUrl}/api/campagne/`,
      {
        send: true,
        capping,
        segments: Object.values(
          segments.reduce(
            (
              res: {
                lists: { [key: number]: any[] };
                indexOfMerge: number | null;
              },
              segment: CampaignSegment,
              index: number,
            ) => {
              // Build an array of category objects for each segment
              const categories = segment.categories.map((category) => ({
                meta_category: category.category.meta_category,
                category: category.category.id,
                pctBase: category.percentage,
              }));

              if (segment.checked) {
                // Merge with existing segment if 'checked' is true
                const pos = res.indexOfMerge ?? index;
                res.lists[pos] = [...(res.lists[pos] || []), ...categories];
                res.indexOfMerge = pos;
              } else {
                // Create a new segment
                const pos = index;
                res.lists[pos] = categories;
              }

              return res;
            },
            {
              lists: {},
              indexOfMerge: null,
            },
          ).lists,
        ),
      },
      {
        observe: 'body',
        responseType: 'text',
      },
    );
  }

  generateSegmentsBlob(capping: number, segments: CampaignSegment[]): Observable<Blob> {
    return this.http.post(
      `${environment.backendUrl}/api/campagne/`,
      {
        send: true,
        capping,
        segments: Object.values(
          segments.reduce(
            (
              res: {
                lists: { [key: number]: any[] };
                indexOfMerge: number | null;
              },
              segment: CampaignSegment,
              index: number,
            ) => {
              // Build an array of category objects for each segment
              const categories = segment.categories.map((category) => ({
                meta_category: category.category.meta_category,
                category: category.category.id,
                pctBase: category.percentage,
              }));

              if (segment.checked) {
                // Merge with existing segment if 'checked' is true
                const pos = res.indexOfMerge ?? index;
                res.lists[pos] = [...(res.lists[pos] || []), ...categories];
                res.indexOfMerge = pos;
              } else {
                // Create a new segment
                const pos = index;
                res.lists[pos] = categories;
              }

              return res;
            },
            {
              lists: {},
              indexOfMerge: null,
            },
          ).lists,
        ),
      },
      {
        observe: 'body',
        responseType: 'blob',
      },
    );
  }

  getTotalMails(): Observable<number> {
    return this.http.get<{ numb_mails: number }>(`${environment.backendUrl}/api/mails/`).pipe(
      map(response => response.numb_mails)
    );
  }

  getColumns(): Observable<Record<string, { type: 'string' | 'number' | 'date', label: string, options?: string[] }>> {
    return this.http.get<any>(`${environment.backendUrl}/api/planning/segment/columns/`);
  }

  suggestName(context: { categories?: string[], filters?: any[], pressure?: number, num_emails?: number }): Observable<{ name: string }> {
    return this.http.post<{ name: string }>(`${environment.backendUrl}/api/planning/segment/suggest-name/`, context);
  }

  previewPlanning(audiences: any[], filters: any[], capping: number, includeCurve: boolean = true, autoPressure: boolean = false): Observable<PostCampagneResponse> {
    return this.http
      .post<PostCampagneResponse>(`${environment.backendUrl}/api/planning/segment/`, {
        audiences,
        filters,
        capping,
        include_curve: includeCurve,
        auto_pressure: autoPressure
      })
      .pipe(
        map((res: PostCampagneResponse) => ({
          ...res,
          venn: res.venn?.map((item: { x: string[]; meta_category: string[]; value: number }) => {
            const xLabels: string[] = [];

            item.x.forEach((xStr, index) => {
              const ids = xStr.split(',').map((id) => id.trim());
              const metaCatStr = item.meta_category[index];
              const metaCats = metaCatStr.split(',').map((mc) => mc.trim());

              const labels = ids.map((id, idx) => {
                const metaCat = parseInt(metaCats[idx]);
                const categoryLabel = this.categoryService.getCategoryById(parseInt(id), metaCat)?.label || '';
                return categoryLabel;
              });

              xLabels.push(labels.join(', '));
            });

            return {
              id: item.x,
              x: xLabels,
              value: item.value,
              meta_category: item.meta_category,
              label: {
                fontColor: '#212529',
                fontWeight: 'normal',
                fontFamily: 'Open Sans, sans-serif',
                fontSize: '15px'
              },
            };
          }) || []
        }))
      );
  }

  getScheduledCampaigns(start: string, end: string): Observable<ScheduledCampaign[]> {
    const params = { start, end };
    return this.http.get<any>(`${environment.backendUrl}/api/scheduled_campaign/`, { params }).pipe(
      map(response => {
        if (response && Array.isArray(response.results)) {
          return response.results;
        } else if (Array.isArray(response)) {
          return response;
        }
        return [];
      })
    );
  }

  createScheduledCampaign(campaign: ScheduledCampaign): Observable<ScheduledCampaign> {
    return this.http.post<ScheduledCampaign>(`${environment.backendUrl}/api/scheduled_campaign/`, campaign);
  }

  updateScheduledCampaign(id: number, campaign: ScheduledCampaign): Observable<ScheduledCampaign> {
    return this.http.put<ScheduledCampaign>(`${environment.backendUrl}/api/scheduled_campaign/${id}/`, campaign);
  }

  patchScheduledCampaign(id: number, data: Partial<ScheduledCampaign>): Observable<ScheduledCampaign> {
    return this.http.patch<ScheduledCampaign>(`${environment.backendUrl}/api/scheduled_campaign/${id}/`, data);
  }

  deleteScheduledCampaign(id: number): Observable<void> {
    return this.http.delete<void>(`${environment.backendUrl}/api/scheduled_campaign/${id}/`);
  }

  downloadScheduledCampaigns(ids: number[], capping: number): Observable<Blob> {
    return this.http.post(
      `${environment.backendUrl}/api/scheduled_campaign/download/`,
      { ids, capping },
      {
        responseType: 'blob',
        observe: 'body'
      }
    );
  }

  pushScheduledCampaigns(ids: number[], capping: number): Observable<{
    results: { campaign: string; list_name: string; contacts: number; status: string; message?: string }[];
  }> {
    return this.http.post<{
      results: { campaign: string; list_name: string; contacts: number; status: string; message?: string }[];
    }>(`${environment.backendUrl}/api/scheduled_campaign/push/`, { ids, capping });
  }

  getOverlap(ids: number[], pressures?: Record<number, number>, maxPerUser?: number, maxPerDay?: number, includeCurves = false): Observable<{
    campaigns: { id: number; name: string; date: string; total: number; scope_count?: number }[];
    intersections: { sets: number[]; count: number }[];
    curves?: Record<string, { x: number; y: number }[]>;
  }> {
    const body: any = { ids };
    if (pressures) body['pressures'] = pressures;
    if (maxPerUser != null) body['max_per_user'] = maxPerUser;
    if (maxPerDay != null) body['max_per_day'] = maxPerDay;
    if (includeCurves) body['include_curves'] = true;
    return this.http.post<any>(
      `${environment.backendUrl}/api/scheduled_campaign/overlap/`,
      body
    );
  }

  optimizeCampaigns(
    ids: number[],
    maxPerUser: number,
    maxPerDay: number,
    pressures: Record<number, number>,
    includeCurves = false,
    filterPriority: 'ia' | 'filter' = 'ia'
  ): Observable<{
    campaigns: {
      id: number; name: string; date: string;
      volume: number; volume_original: number; removed: number;
      volume_pct: number; lift: number; potential: number;
      scope_count?: number;
    }[];
    overlap: {
      campaigns: { id: number; name: string; total: number }[];
      intersections: { sets: number[]; count: number }[];
    };
    curves?: Record<string, { x: number; y: number }[]>;
  }> {
    const body: any = { ids, max_per_user: maxPerUser, max_per_day: maxPerDay, pressures, filter_priority: filterPriority };
    if (includeCurves) body['include_curves'] = true;
    return this.http.post<any>(
      `${environment.backendUrl}/api/scheduled_campaign/optimize/`,
      body
    );
  }

  getSegmentMetadata(channel: string = 'all'): Observable<SegmentMetadata> {
    return this.http.get<SegmentMetadata>(
      `${environment.backendUrl}/api/planning/segment/metadata/?channel=${encodeURIComponent(channel)}`
    );
  }

  getSegmentMatrix(channel: string = 'all'): Observable<SegmentMatrixResponse> {
    return this.http.get<SegmentMatrixResponse>(
      `${environment.backendUrl}/api/segment-matrix/?channel=${encodeURIComponent(channel)}`
    );
  }
}
